import {
  ArrowBendDownLeftIcon,
  ArrowsMergeIcon,
  NotebookIcon,
  QuestionIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EmptyState } from '@/components/empty-state';
import { StatusBadge } from '@/components/status-badge';
import { Popover } from '@/components/popover';
import { HorizontalScrollbar } from '@/components/horizontal-scrollbar';
import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import type { Flight, LogbookEntry, LogbookEntryMergeSource } from '@/lib/db';
import { aircraft, flights, logbookEntries, orderEntriesChronologically, settings } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';

import { CAA_1373_COLUMNS, CAA_1373_GROUPS, CAA_1373_SUBGROUPS } from './caa-columns';
import {
  columnById,
  entryTotalHours,
  columnHeaderName,
  LOGBOOK_COLUMN_GROUPS,
  LOGBOOK_COLUMN_SUBGROUPS,
  LOGBOOK_COLUMNS,
} from './columns';
import { applyAircraftMatch } from './draft';
import { FlightEntryForm } from './FlightEntryForm';
import { applyCellEdit, confirmEntry, createManualEntry, unconfirmEntry } from './entry';
import { describeMerge, mergeBlockedReason, mergeEntries } from './merge';
import { reviewFlags } from './review';
import { LOGBOOK_OPENING_BALANCE_KEY } from './settings-key';
import { formatDecimalHours } from './time';
import { computeRunningTotals } from './totals';
import { TrackLinkCell } from './TrackLinkCell';

/**
 * The logbook page — `PLAN.md` §6 Phase 5: a spreadsheet, not a form. Every
 * row is one `LogbookEntry`; every cell reads and writes through the same
 * column list the header renders from, which is what keeps keyboard
 * navigation, fill-down and undo honest about what's actually on screen.
 *
 * Two column lists, one grid. `LOGBOOK_COLUMNS` is the flight record — the
 * facts a pilot enters, editable. `CAA_1373_COLUMNS` is the same rows as CAA
 * Form 1373 prints them, with the single/multi × day/night × capacity cells
 * derived and read-only. The toggle between them is the point of the
 * redesign made visible: the same logbook, entered simply and presented the
 * way a NZ pilot expects to see it.
 *
 * Only one thing here is not a cell edit: approving a draft. That's a
 * separate action rather than a checkbox in the grid because it changes what
 * an entry *means* — `NZ-LOGBOOK.md` §2.3 — not just what it says.
 */

/** Which of the two column lists the grid is showing. */
export type LogbookView = 'list' | 'record' | 'caa';

const CELL_CLASS =
  'flex h-8 w-full min-w-0 items-center truncate px-2 text-left text-sm outline-none focus-visible:relative focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring';
const CELL_INPUT_CLASS =
  'h-8 w-full min-w-0 border-0 bg-transparent px-2 text-sm text-foreground outline-none focus-visible:relative focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring';

/** Ties the grid's scroll container to the scrollbar's `aria-controls`. */
const SCROLL_AREA_ID = 'logbook-grid-scroll-area';

function cellKey(entryId: string, columnId: string): string {
  return `${entryId}::${columnId}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

interface Snapshot {
  id: string;
  before: LogbookEntry;
}

/** What a merged row's badge says when a pilot asks it where its hours came from. */
function mergedTooltip(sources: readonly LogbookEntryMergeSource[]): string {
  const legs = sources
    .map((source) => `${source.date} (${formatDecimalHours(source.hours)} hr)`)
    .join(', ');
  return `Added together from ${String(sources.length + 1)} recordings of one flight. Folded in: ${legs}. The tracks behind them are still on the flights page.`;
}

/** A stable empty array, so a database that hasn't answered yet doesn't recreate `rows` on every render. */
const EMPTY_ROWS: readonly LogbookEntry[] = [];

/** A stable empty map, for the rows the flag pass hasn't reached — same reason as `EMPTY_ROWS`. */
const NO_FLAGS: ReadonlyMap<string, string> = new Map();

/** Below Tailwind's `sm`, where the grid shows two columns of thirty. */
function isNarrowScreen(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
}

/**
 * The grid's rules, on request rather than under the table.
 *
 * They used to be printed below the grid as eight labelled paragraphs — on a
 * phone, longer than the grid itself. A pilot needs them once; the reason a
 * particular cell is red is in that cell's own tooltip.
 */
function GridHelp() {
  return (
    <Popover
      panelClassName="w-80 max-w-[calc(100vw-2rem)] p-3"
      trigger={({ open, toggle }) => (
        <Button
          size="icon"
          variant="ghost"
          aria-label="Logbook help"
          aria-expanded={open}
          onClick={toggle}
        >
          <QuestionIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
        </Button>
      )}
    >
      <dl className="space-y-2 text-xs" data-testid="logbook-help">
        <div>
          <dt className="font-medium">Editing</dt>
          <dd className="text-muted-foreground">Double-click or press Enter to edit a cell.</dd>
        </div>
        <div>
          <dt className="font-medium">Bulk actions</dt>
          <dd className="text-muted-foreground">Select rows to fill down, approve or merge.</dd>
        </div>
        <div>
          <dt className="font-medium">Shortcuts</dt>
          <dd className="text-muted-foreground">Ctrl/⌘+Z undo. Ctrl/⌘+D fill down.</dd>
        </div>
        <div>
          <dt className="font-medium">Validation</dt>
          <dd className="text-muted-foreground">
            Red cells need checking. Hover for the reason. Warnings do not prevent approval.
          </dd>
        </div>
        <div>
          <dt className="font-medium">Input formats</dt>
          <dd className="text-muted-foreground">
            Date YYYY-MM-DD. Hours <span className="tabular">1:30</span>,{' '}
            <span className="tabular">1.5</span> or <span className="tabular">90m</span>. Aerodrome:
            ICAO code.
          </dd>
        </div>
        <div>
          <dt className="font-medium">CAA 1373</dt>
          <dd className="text-muted-foreground">Computed from the flight record. Read-only.</dd>
        </div>
        <div>
          <dt className="font-medium">To date</dt>
          <dd className="text-muted-foreground">* marks a draft. Drafts are not counted.</dd>
        </div>
      </dl>
    </Popover>
  );
}

/**
 * The logbook as a list, for a phone.
 *
 * At 390px the grid showed Date and From and then a scrollbar: every other
 * column was out of sight, and editing meant finding a cell by scrolling
 * blind. A row here is the handful of fields that tell entries apart, and
 * tapping one opens the flight form — the same form "Add flight" uses, which
 * already fits a phone.
 */
function EntryList({
  rows,
  flagsById,
  className,
  onOpen,
  onToggleConfirm,
}: {
  rows: readonly LogbookEntry[];
  flagsById: ReadonlyMap<string, ReadonlyMap<string, string>>;
  className: string;
  onOpen: (entry: LogbookEntry) => void;
  onToggleConfirm: (entry: LogbookEntry) => void;
}) {
  return (
    <ul
      className={`border-border bg-surface rounded-container divide-border divide-y border ${className}`}
      data-testid="logbook-list"
    >
      {rows.map((entry) => {
        const flagged = (flagsById.get(entry.id)?.size ?? 0) > 0;
        const route =
          entry.from === undefined && entry.to === undefined
            ? '—'
            : `${entry.from ?? '—'} → ${entry.to ?? '—'}`;
        return (
          <li
            key={entry.id}
            id={entry.id}
            className="target:bg-accent-soft flex items-center gap-3 px-3 py-2.5"
          >
            <button
              type="button"
              className="min-w-0 flex-1 text-left disabled:cursor-default"
              disabled={!entry.isDraft}
              title={entry.isDraft ? undefined : 'Approved. Move back to draft to edit.'}
              onClick={() => {
                onOpen(entry);
              }}
            >
              <span className="flex items-baseline justify-between gap-3">
                <span className="tabular text-sm">{entry.date}</span>
                <span className="tabular text-sm font-semibold">
                  {formatDecimalHours(entryTotalHours(entry))}
                </span>
              </span>
              <span className="text-muted-foreground mt-0.5 flex items-baseline justify-between gap-3 text-xs">
                <span className="truncate">
                  {route}
                  {entry.registration === undefined ? '' : ` · ${entry.registration}`}
                </span>
                {flagged ? <span className="text-danger shrink-0">Check figures</span> : null}
              </span>
            </button>
            <button
              type="button"
              className="rounded-control hover:bg-surface-sunken shrink-0 px-1.5 py-1"
              aria-label={
                entry.isDraft
                  ? `Approve entry for ${entry.date}`
                  : `Move entry for ${entry.date} back to draft`
              }
              onClick={() => {
                onToggleConfirm(entry);
              }}
            >
              <StatusBadge status={entry.isDraft ? 'draft' : 'approved'} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export interface LogbookGridProps {
  /**
   * The entry to open on: its row is ticked, its Date cell focused, and the
   * browser scrolls to it.
   *
   * A flight page's "Review entry" button lands here, and landing at the top
   * of a hundred-row logbook told only that the entry is *somewhere* below is
   * no better than not linking at all. The route passes the URL hash; the
   * selection is applied once per id, so the pilot can untick the row and it
   * stays unticked.
   */
  focusEntryId?: string;
}

export function LogbookGrid({ focusEntryId }: LogbookGridProps = {}) {
  /** The linked entry, with the empty hash of a plain `/logbook` visit read as "no entry". */
  const linkedEntryId = focusEntryId === undefined || focusEntryId === '' ? null : focusEntryId;

  const entries = useLiveQuery(() => logbookEntries.listOrdered(), [], null);
  const openingBalance = useLiveQuery(() => settings.get(LOGBOOK_OPENING_BALANCE_KEY, 0), [], null);
  /*
   * Every flight, fetched once for the page rather than once per row: Dexie
   * re-runs a live query whenever a table it read changes, so a per-row
   * lookup would be N subscriptions all firing on the same write. The grid
   * needs this both for the takeoff/landing review gate and for the track
   * link column.
   */
  const allFlights = useLiveQuery(() => flights.list(), [], null);
  /** All three live queries have answered, so the table below is real rather than a placeholder. */
  const gridReady = entries !== null && openingBalance !== null && allFlights !== null;

  /*
   * The linked row starts ticked and its Date cell starts selected — seeded
   * here at mount rather than assigned from an effect, so the grid never
   * paints once with nothing selected and then corrects itself. The route
   * keys this component on the hash, so a link to a different entry is a
   * fresh mount and seeds again; after that the selection is the pilot's,
   * and unticking the row leaves it unticked.
   */
  const [checked, setChecked] = useState<ReadonlySet<string>>(() =>
    linkedEntryId === null ? new Set() : new Set([linkedEntryId]),
  );
  const [lastCheckedId, setLastCheckedId] = useState<string | null>(linkedEntryId);
  /*
   * The grid opens on the flight record, not the 1373 table, because the
   * first thing a pilot does here is enter a flight and the 1373 table has
   * nothing to type into. The toggle is one click away, and the printed page
   * is always the 1373 one.
   */
  const [view, setView] = useState<LogbookView>(() => (isNarrowScreen() ? 'list' : 'record'));
  /** The list is a phone layout; widening the window past it falls back to the grid. */
  const gridView: Exclude<LogbookView, 'list'> = view === 'caa' ? 'caa' : 'record';
  /**
   * The flight form, when it is open: `'new'` to add one, or the entry being
   * edited. The grid stays the place to reach any cell; this is the fast
   * path for a whole flight at once, and the only place role and purpose can
   * be set.
   */
  const [formFor, setFormFor] = useState<LogbookEntry | 'new' | null>(null);
  const columns = gridView === 'caa' ? CAA_1373_COLUMNS : LOGBOOK_COLUMNS;
  const groupRuns = gridView === 'caa' ? CAA_1373_GROUPS : LOGBOOK_COLUMN_GROUPS;
  const subGroupRuns = gridView === 'caa' ? CAA_1373_SUBGROUPS : LOGBOOK_COLUMN_SUBGROUPS;
  const [selected, setSelected] = useState<{ entryId: string; columnId: string } | null>(
    linkedEntryId === null ? null : { entryId: linkedEntryId, columnId: 'date' },
  );
  const [editing, setEditing] = useState<{ entryId: string; columnId: string } | null>(null);
  const [editText, setEditText] = useState('');
  /** The width the cell being edited had as a button, in px — see `startEdit`. */
  const [editWidth, setEditWidth] = useState<number | null>(null);
  const [invalidCell, setInvalidCell] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [mergeNote, setMergeNote] = useState<string | null>(null);

  const undoStack = useRef<Snapshot[][]>([]);
  const cellRefs = useRef(new Map<string, HTMLElement>());
  /** Set on `pointerdown`, read on the `click` that follows — see the cell button below. */
  const wasFocused = useRef(false);

  /*
   * Ordered here as well as in the repository, because the "to date" column
   * next to each row is only that row's running total if the grid and
   * `computeRunningTotals` walk the entries in the same sequence. Stating the
   * order where the rows are rendered keeps that invariant local rather than
   * resting on a query two layers away.
   */
  const flightById = useMemo(
    () => new Map((allFlights ?? []).map((flight) => [flight.id, flight])),
    [allFlights],
  );
  const rows = useMemo(
    () => (entries === null ? EMPTY_ROWS : orderEntriesChronologically(entries, flightById)),
    [entries, flightById],
  );
  const totals = useMemo(
    () => computeRunningTotals(rows, openingBalance ?? 0, flightById),
    [rows, openingBalance, flightById],
  );
  const totalById = useMemo(() => new Map(totals.map((t) => [t.entryId, t])), [totals]);
  /** Cell id → why it's red, per row. Empty means the row is clean. */
  const flagsById = useMemo(
    () => new Map(rows.map((row) => [row.id, reviewFlags(row, flightById.get(row.flightId))])),
    [rows, flightById],
  );
  const checkedRows = useMemo(() => rows.filter((row) => checked.has(row.id)), [rows, checked]);
  const mergeBlocked = useMemo(() => mergeBlockedReason(checkedRows), [checkedRows]);
  const checkedNeedsReviewCount = useMemo(
    () =>
      rows.filter(
        (row) => checked.has(row.id) && row.isDraft && (flagsById.get(row.id)?.size ?? 0) > 0,
      ).length,
    [rows, checked, flagsById],
  );

  /*
   * The grid is almost always wider than the screen — it holds every CAR
   * 61.29 column at once, by design — so it carries its own always-visible,
   * draggable scrollbar underneath the table and hides the platform's.
   * See `components/horizontal-scrollbar`.
   */
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const registerCell = useCallback((key: string, el: HTMLElement | null) => {
    if (el) {
      cellRefs.current.set(key, el);
    } else {
      cellRefs.current.delete(key);
    }
  }, []);

  /*
   * Focusing the next cell has to wait for the row that was mid-edit to
   * re-render as a plain button, which hasn't happened yet at the point a
   * key handler runs — so this is a `setTimeout`, not a direct `.focus()`,
   * and deliberately not a `focusRequest` state changed from inside a
   * `useEffect`, which only trades one render-after-commit problem for
   * another.
   */
  const scheduleFocus = useCallback((key: string) => {
    window.setTimeout(() => {
      cellRefs.current.get(key)?.focus();
    }, 0);
  }, []);

  /*
   * Putting the cursor in the linked row's Date cell is the other half of
   * arriving from a flight's "Review entry" button, and unlike the tick it
   * cannot be seeded at mount: the grid renders a placeholder until Dexie
   * answers, so the cell does not exist yet.
   *
   * The test is the cell's own ref, not "is the entry in `rows`". Those are
   * not the same moment: `entries`, `allFlights` and the opening balance are
   * three independent live queries, and the first of them to land puts the
   * row in `rows` while the grid is still showing its placeholder. A cell
   * that isn't registered yet is a render still to come, so the effect waits
   * for the next one rather than spending its one attempt on nothing.
   */
  const cursorPlaced = useRef(false);
  useEffect(() => {
    if (linkedEntryId === null || cursorPlaced.current) {
      return;
    }
    const cell = cellRefs.current.get(cellKey(linkedEntryId, 'date'));
    if (cell === undefined) {
      return;
    }
    cursorPlaced.current = true;
    // Straight `.focus()`, not `scheduleFocus`: an effect already runs after
    // the commit that created this element. Focusing is also what scrolls the
    // row into view, on top of the browser's own jump to the `#id` anchor.
    cell.focus();
  }, [linkedEntryId, gridReady, rows]);

  const pushUndo = useCallback((snapshot: Snapshot[]) => {
    if (snapshot.length === 0) {
      return;
    }
    undoStack.current = [...undoStack.current.slice(-49), snapshot];
    setCanUndo(true);
  }, []);

  const undo = useCallback(async () => {
    const action = undoStack.current[undoStack.current.length - 1];
    if (action === undefined) {
      return;
    }
    undoStack.current = undoStack.current.slice(0, -1);
    setCanUndo(undoStack.current.length > 0);
    await Promise.all(action.map(({ before }) => logbookEntries.put(before)));
  }, []);

  const commit = useCallback(
    async (entryId: string, columnId: string, raw: string): Promise<boolean> => {
      const entry = rows.find((row) => row.id === entryId);
      if (entry === undefined) {
        return false;
      }
      const result = applyCellEdit(entry, columnId, raw, nowIso());
      if (!result.ok || result.entry === undefined) {
        const key = cellKey(entryId, columnId);
        setInvalidCell(key);
        window.setTimeout(() => {
          setInvalidCell((current) => (current === key ? null : current));
        }, 1200);
        return false;
      }
      if (result.entry !== entry) {
        pushUndo([{ id: entryId, before: entry }]);
        let toStore = result.entry;
        // A recognised registration fills in the aircraft type and engine
        // class automatically — the NZ-style SE/ME/etc hour columns then
        // fill themselves too, with nothing further to pick by hand.
        if (columnId === 'registration' && toStore.registration !== undefined) {
          const match = await aircraft.findByRegistration(toStore.registration);
          toStore = applyAircraftMatch(toStore, match, nowIso());
        }
        await logbookEntries.put(toStore);
      }
      return true;
    },
    [rows, pushUndo],
  );

  const startEdit = useCallback((entryId: string, columnId: string, initial: string) => {
    /*
     * Measured off the button that is about to be replaced, because it is
     * the button's un-wrapped text that has been holding this column open —
     * see the editing cell below. Read here rather than in an effect: after
     * the swap the width to preserve is already gone. In jsdom every box is
     * 0×0, which reads as "nothing to freeze" and is exactly right.
     */
    const width = cellRefs.current.get(cellKey(entryId, columnId))?.getBoundingClientRect().width;
    setEditWidth(width === undefined || width === 0 ? null : Math.round(width));
    setEditing({ entryId, columnId });
    // Sanitised on the way in as well as on every keystroke: editing starts
    // from whatever key was pressed, and a letter typed at a date cell should
    // open it empty rather than open it wrong.
    const column = columnById(columnId);
    setEditText(column?.input === undefined ? initial : column.input.sanitise(initial));
  }, []);

  const computeNeighbourKey = useCallback(
    (entryId: string, columnId: string, dRow: number, dCol: number): string | null => {
      const rowIndex = rows.findIndex((row) => row.id === entryId);
      const colIndex = columns.findIndex((column) => column.id === columnId);
      if (rowIndex === -1 || colIndex === -1) {
        return null;
      }
      const nextRow = Math.min(Math.max(rowIndex + dRow, 0), rows.length - 1);
      const nextCol = Math.min(Math.max(colIndex + dCol, 0), columns.length - 1);
      const nextEntry = rows[nextRow];
      const nextColumn = columns[nextCol];
      if (nextEntry === undefined || nextColumn === undefined) {
        return null;
      }
      return cellKey(nextEntry.id, nextColumn.id);
    },
    [rows, columns],
  );

  const moveFocus = useCallback(
    (entryId: string, columnId: string, dRow: number, dCol: number) => {
      const key = computeNeighbourKey(entryId, columnId, dRow, dCol);
      if (key !== null) {
        cellRefs.current.get(key)?.focus();
      }
    },
    [computeNeighbourKey],
  );

  const commitAndMove = useCallback(
    async (entryId: string, columnId: string, raw: string, dRow: number, dCol: number) => {
      const ok = await commit(entryId, columnId, raw);
      if (!ok) {
        return;
      }
      setEditing(null);
      const key = computeNeighbourKey(entryId, columnId, dRow, dCol);
      scheduleFocus(key ?? cellKey(entryId, columnId));
    },
    [commit, computeNeighbourKey, scheduleFocus],
  );

  const cancelEdit = useCallback(
    (entryId: string, columnId: string) => {
      setEditing(null);
      scheduleFocus(cellKey(entryId, columnId));
    },
    [scheduleFocus],
  );

  const toggleChecked = useCallback(
    (entryId: string, range: boolean) => {
      setChecked((current) => {
        const next = new Set(current);
        if (range && lastCheckedId !== null) {
          const ids = rows.map((row) => row.id);
          const from = ids.indexOf(lastCheckedId);
          const to = ids.indexOf(entryId);
          if (from !== -1 && to !== -1) {
            const [start, end] = from < to ? [from, to] : [to, from];
            for (let i = start; i <= end; i += 1) {
              const id = ids[i];
              if (id !== undefined) {
                next.add(id);
              }
            }
            return next;
          }
        }
        if (next.has(entryId)) {
          next.delete(entryId);
        } else {
          next.add(entryId);
        }
        return next;
      });
      setLastCheckedId(entryId);
    },
    [rows, lastCheckedId],
  );

  const fillDown = useCallback(async () => {
    if (selected === null || checked.size < 2) {
      return;
    }
    const column = columnById(selected.columnId);
    if (!column) {
      return;
    }
    const source = checkedRows[0];
    if (source === undefined) {
      return;
    }
    const value = column.read(source);
    const match =
      column.id === 'registration' && value !== ''
        ? await aircraft.findByRegistration(value)
        : undefined;
    const snapshot: Snapshot[] = [];
    const writes: LogbookEntry[] = [];
    for (const target of checkedRows.slice(1)) {
      const result = applyCellEdit(target, column.id, value, nowIso());
      if (result.ok && result.entry !== undefined && result.entry !== target) {
        const finalEntry =
          match === undefined ? result.entry : applyAircraftMatch(result.entry, match, nowIso());
        snapshot.push({ id: target.id, before: target });
        writes.push(finalEntry);
      }
    }
    if (writes.length === 0) {
      return;
    }
    pushUndo(snapshot);
    await Promise.all(writes.map((entry) => logbookEntries.put(entry)));
  }, [selected, checked, checkedRows, pushUndo]);

  const bulkConfirm = useCallback(
    async (confirm: boolean) => {
      const targets = rows.filter((row) => checked.has(row.id) && row.isDraft === confirm);
      if (targets.length === 0) {
        return;
      }
      const now = nowIso();
      pushUndo(targets.map((entry) => ({ id: entry.id, before: entry })));
      await Promise.all(
        targets.map((entry) =>
          logbookEntries.put(confirm ? confirmEntry(entry, now) : unconfirmEntry(entry, now)),
        ),
      );
    },
    [rows, checked, pushUndo],
  );

  /**
   * Deletes both the entry and the flight behind it.
   *
   * A flight exists to join a track, an aircraft and a logbook line — drop the
   * line without it and the flight is left with nothing to join, which
   * `buildFlightSummaries` still renders as a permanent "No entry / Unknown
   * aircraft" row on the flights page. A track behind it is untouched: it
   * reappears there as an unclaimed track, exactly as it would have before
   * import ever drafted this entry.
   */
  const bulkDelete = useCallback(async () => {
    const targets = rows.filter((row) => checked.has(row.id));
    if (targets.length === 0) {
      return;
    }
    await Promise.all(
      targets.map(async (entry) => {
        await logbookEntries.delete(entry.id);
        await flights.delete(entry.flightId);
      }),
    );
    setChecked(new Set());
  }, [checked, rows]);

  /**
   * Fold the checked rows into one entry — two recordings of the same flight,
   * added together rather than one of them thrown away (`merge.ts`).
   *
   * The absorbed entries are deleted here but snapshotted first, and Dexie's
   * `put` re-creates a deleted row, so the undo stack reverses a merge the
   * same way it reverses a keystroke. Their flights and tracks are left
   * alone: the evidence stays, only the logbook line is consolidated.
   */
  const mergeChecked = useCallback(async () => {
    const outcome = mergeEntries(checkedRows, nowIso());
    if (!outcome.ok) {
      setMergeNote(outcome.reason);
      return;
    }
    pushUndo([
      {
        id: outcome.entry.id,
        before: checkedRows.find((row) => row.id === outcome.entry.id) ?? outcome.entry,
      },
      ...outcome.absorbed.map((entry) => ({ id: entry.id, before: entry })),
    ]);
    await logbookEntries.put(outcome.entry);
    await Promise.all(outcome.absorbed.map((entry) => logbookEntries.delete(entry.id)));
    setChecked(new Set([outcome.entry.id]));
    setMergeNote(describeMerge(outcome));
  }, [checkedRows, pushUndo]);

  const addEntry = useCallback(async () => {
    const now = nowIso();
    const today = formatLocalDate(now, { format: 'iso' });
    const flight: Flight = {
      id: crypto.randomUUID(),
      date: today,
      derived: { confidence: 'low' },
      createdAt: now,
      updatedAt: now,
    };
    await flights.put(flight);
    const entry = createManualEntry({ flightId: flight.id, date: today }, now);
    await logbookEntries.put(entry);
    scheduleFocus(cellKey(entry.id, 'date'));
  }, [scheduleFocus]);

  const toggleOneConfirm = useCallback(
    async (entry: LogbookEntry) => {
      const now = nowIso();
      pushUndo([{ id: entry.id, before: entry }]);
      await logbookEntries.put(
        entry.isDraft ? confirmEntry(entry, now) : unconfirmEntry(entry, now),
      );
    },
    [pushUndo],
  );

  if (!gridReady) {
    return <p className="text-muted-foreground text-sm">Opening the local database…</p>;
  }

  /*
   * The form sits above the grid rather than in a modal: on a phone it is
   * the whole screen anyway, and on a desktop a pilot filling one in can
   * still read the rows they are adding to. Rendered in both branches below
   * so "Add flight" works from an empty logbook too.
   */
  const formPanel =
    formFor === null ? null : (
      <FlightEntryForm
        {...(formFor === 'new' ? {} : { entry: formFor })}
        onDone={(entryId) => {
          setFormFor(null);
          scheduleFocus(cellKey(entryId, 'date'));
        }}
        onCancel={() => {
          setFormFor(null);
        }}
      />
    );

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {formPanel}
        <EmptyState
          icon={NotebookIcon}
          title="No logbook entries"
          description="Imported flights create draft entries. Add an entry to record a flight by hand."
        >
          <Button
            onClick={() => {
              setFormFor('new');
            }}
          >
            <PlusIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Add flight
          </Button>
          <Button
            variant="ghost"
            title="Add an empty row and fill it in cell by cell"
            onClick={() => {
              void addEntry();
            }}
          >
            Blank row
          </Button>
        </EmptyState>
      </div>
    );
  }

  const allChecked = checked.size > 0 && checked.size === rows.length;
  const confirmedToDate = totals[totals.length - 1]?.total ?? openingBalance;
  const drafts = rows.filter((row) => row.isDraft);
  const draftCount = drafts.length;
  const draftHours = drafts.reduce((sum, row) => sum + entryTotalHours(row), 0);

  return (
    <div className="space-y-3">
      {formPanel}
      {/*
        The total says what it leaves out. Straight after an import every row
        is a draft, and a bare "0.00" beside ten hours of flying read as a
        bug rather than as work waiting to be approved. Brought forward is
        set on the Totals tab — it is entered once, not every visit.
      */}
      <p className="text-sm" data-testid="logbook-total">
        <span className="text-muted-foreground">Total to date </span>
        <span className="tabular font-semibold">{formatDecimalHours(confirmedToDate)}</span>
        {draftCount === 0 ? null : (
          <span className="text-muted-foreground">
            {' · '}
            {draftCount} {draftCount === 1 ? 'draft' : 'drafts'} (
            <span className="tabular">{formatDecimalHours(draftHours)}</span> hr) not included
          </span>
        )}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setFormFor('new');
          }}
        >
          <PlusIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Add flight
        </Button>
        <Button
          size="sm"
          variant="ghost"
          title="Add an empty row and fill it in cell by cell"
          onClick={() => {
            void addEntry();
          }}
        >
          Blank row
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canUndo}
          onClick={() => {
            void undo();
          }}
        >
          <ArrowBendDownLeftIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Undo
        </Button>
        {/*
         * One control, two tables. Named for what each one *is* rather than
         * "simple/advanced": the flight record is what happened, CAA 1373
         * is how the CAA asks to see it, and neither is a reduced version
         * of the other.
         */}
        <div className="ml-auto flex items-center gap-1">
          <GridHelp />
          <div
            role="group"
            aria-label="Logbook columns"
            className="border-border rounded-control flex items-center border p-0.5"
          >
            {/*
            The list exists for a phone, where the grid shows two columns of
            thirty and the rest is a sideways scroll. It is only offered
            there; from `sm` up the grid fits well enough to work in.
          */}
            <Button
              size="sm"
              className="sm:hidden"
              variant={view === 'list' ? 'outline' : 'ghost'}
              aria-pressed={view === 'list'}
              onClick={() => {
                setView('list');
              }}
            >
              List
            </Button>
            <Button
              size="sm"
              variant={view === 'record' ? 'outline' : 'ghost'}
              aria-pressed={view === 'record'}
              onClick={() => {
                setView('record');
              }}
            >
              Flight record
            </Button>
            <Button
              size="sm"
              variant={view === 'caa' ? 'outline' : 'ghost'}
              aria-pressed={view === 'caa'}
              title="CAA 1373 columns, derived from the flight record. Read-only."
              onClick={() => {
                setView('caa');
              }}
            >
              CAA 1373
            </Button>
          </div>
        </div>
        {checked.size > 0 ? (
          <div className="border-border bg-surface-sunken rounded-control flex flex-wrap items-center gap-2 border px-2.5 py-1">
            <span className="text-muted-foreground text-xs">{checked.size} selected</span>
            <Button
              size="sm"
              variant="ghost"
              disabled={checked.size !== 1 || checkedRows[0]?.isDraft !== true}
              title={
                checkedRows[0]?.isDraft === false
                  ? 'Approved. Move this entry back to draft to edit it.'
                  : 'Open this flight in the entry form'
              }
              onClick={() => {
                const only = checkedRows[0];
                if (only?.isDraft === true) {
                  setFormFor(only);
                }
              }}
            >
              Edit flight
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={selected === null || checked.size < 2}
              title={
                selected === null
                  ? 'Click a cell first, then select rows to fill down into'
                  : undefined
              }
              onClick={() => {
                void fillDown();
              }}
            >
              Fill down
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={mergeBlocked !== undefined}
              title={
                mergeBlocked ??
                'Add the selected entries together into one. Hours, takeoffs and landings summed.'
              }
              onClick={() => {
                void mergeChecked();
              }}
            >
              <ArrowsMergeIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
              Merge
            </Button>
            <Button
              size="sm"
              variant="ghost"
              title={
                checkedNeedsReviewCount > 0
                  ? `${String(checkedNeedsReviewCount)} of the selected entries have flagged takeoff/landing counts. Approving anyway.`
                  : undefined
              }
              onClick={() => {
                void bulkConfirm(true);
              }}
            >
              Approve
            </Button>
            {checkedNeedsReviewCount > 0 ? (
              <span className="text-danger text-xs">
                {checkedNeedsReviewCount} still flagged for review
              </span>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void bulkConfirm(false);
              }}
            >
              Back to draft
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void bulkDelete();
              }}
            >
              <TrashIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setChecked(new Set());
                setMergeNote(null);
              }}
            >
              Clear
            </Button>
          </div>
        ) : null}
      </div>

      {mergeNote === null ? null : (
        <p role="status" className="text-muted-foreground text-xs">
          {mergeNote}
        </p>
      )}

      {view === 'list' ? (
        <EntryList
          rows={rows}
          flagsById={flagsById}
          className="sm:hidden"
          onOpen={(entry) => {
            setFormFor(entry);
          }}
          onToggleConfirm={(entry) => {
            void toggleOneConfirm(entry);
          }}
        />
      ) : null}
      <div
        className={`border-border rounded-container overflow-hidden border ${view === 'list' ? 'hidden sm:block' : ''}`}
      >
        <div
          ref={scrollAreaRef}
          id={SCROLL_AREA_ID}
          className="scrollbar-none overflow-x-auto"
          data-testid="logbook-grid-scroll-area"
        >
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-border bg-surface-sunken border-b text-xs">
                <th scope="col" className="w-8 px-2 py-1.5">
                  <input
                    type="checkbox"
                    aria-label="Select all entries"
                    checked={allChecked}
                    onChange={() => {
                      setChecked(allChecked ? new Set() : new Set(rows.map((row) => row.id)));
                    }}
                  />
                </th>
                {/*
                  Blank up here: Status and Track name columns, so they sit in
                  the bottom row with every other column name. In the group
                  row they read as two more groups and the header misaligned.
                */}
                <th scope="col" className="w-24" />
                <th scope="col" className="w-24" />
                {groupRuns.map((group, index) => (
                  <th
                    key={`${group.label}-${String(index)}`}
                    scope="colgroup"
                    colSpan={group.span}
                    className="text-muted-foreground border-border border-l px-2 py-1 text-left font-medium tracking-wide uppercase"
                  >
                    {group.label}
                  </th>
                ))}
                <th scope="col" className="border-border w-20 border-l" />
              </tr>
              {/*
               * 1373's middle band: DAY and NIGHT inside SINGLE ENGINE and
               * MULTI ENGINE, blank everywhere else. Rendered even when
               * every cell in it is blank, so the column names below never
               * shift up a row between the two views and the header keeps
               * the shape of the paper page.
               */}
              <tr className="border-border text-muted-foreground border-b text-xs">
                <th scope="col" />
                <th scope="col" />
                <th scope="col" />
                {subGroupRuns.map((run, index) =>
                  run.label === '' ? (
                    <th key={`blank-${String(index)}`} colSpan={run.span} />
                  ) : (
                    <th
                      key={`${run.label}-${String(index)}`}
                      scope="colgroup"
                      colSpan={run.span}
                      className="border-border border-l px-2 py-1 text-left font-medium"
                    >
                      {run.label}
                    </th>
                  ),
                )}
                <th scope="col" />
              </tr>
              <tr className="border-border text-muted-foreground border-b text-xs">
                <th scope="col" />
                <th scope="col" className="px-2 py-1 text-left font-normal">
                  Status
                </th>
                <th scope="col" className="px-2 py-1 text-left font-normal">
                  Track
                </th>
                {columns.map((column) => {
                  const name = columnHeaderName(columns, column);
                  return (
                    <th
                      key={column.id}
                      scope="col"
                      // Qualified only where the bare label repeats — see
                      // `columnHeaderName`. The cell still *shows* the short
                      // label the paper page prints.
                      {...(name === column.label ? {} : { 'aria-label': name })}
                      className={`${column.width} px-2 py-1 font-normal ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      {column.label}
                    </th>
                  );
                })}
                <th scope="col" className="border-border border-l px-2 py-1 text-right font-normal">
                  To date
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => {
                const running = totalById.get(entry.id);
                const merged =
                  entry.mergedFrom === undefined || entry.mergedFrom.length === 0
                    ? undefined
                    : entry.mergedFrom;
                const flags = flagsById.get(entry.id) ?? NO_FLAGS;
                const needsReview = flags.size > 0;
                const isChecked = checked.has(entry.id);
                return (
                  <tr
                    key={entry.id}
                    // The row is an anchor target: the flight page and the flight
                    // list both link straight to the entry they are about, and a
                    // logbook that opened at the top would make the reader hunt
                    // for it. The tick that arrives with that link is what makes
                    // the row findable once the reader looks up from the scroll.
                    id={entry.id}
                    className={`border-border hover:bg-surface-sunken/60 target:bg-accent-soft scroll-mt-24 border-b last:border-0 ${isChecked ? 'bg-accent-soft/50' : ''}`}
                  >
                    <td className="px-2 py-1 align-middle">
                      <input
                        type="checkbox"
                        aria-label={`Select entry for ${entry.date}`}
                        checked={isChecked}
                        onClick={(e) => {
                          toggleChecked(entry.id, e.shiftKey);
                        }}
                        onChange={() => undefined}
                      />
                    </td>
                    <td className="px-2 py-1 align-middle">
                      {/* A span, not a flex `td`: a table cell told to lay itself out as a flex container stops being a table cell, and the column widths go with it. */}
                      <span className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            void toggleOneConfirm(entry);
                          }}
                          className="rounded-control text-muted-foreground hover:bg-surface-sunken hover:text-foreground inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs"
                          title={
                            entry.isDraft && needsReview
                              ? 'Some cells are flagged for review (highlighted in red). Approve anyway, or check them first.'
                              : entry.isDraft
                                ? 'Drafted from the imported track. Review and approve to count it.'
                                : 'Approved. Counts toward totals. Click to put back in review.'
                          }
                        >
                          <span
                            aria-hidden="true"
                            className={`size-1.5 rounded-full ${entry.isDraft ? 'bg-muted-foreground' : 'bg-accent'}`}
                          />
                          {entry.isDraft ? 'Draft' : 'Approved'}
                        </button>
                        {merged === undefined ? null : (
                          <span
                            className="text-muted-foreground border-border rounded-control border px-1 text-[0.625rem]"
                            title={mergedTooltip(merged)}
                          >
                            +{merged.length}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <TrackLinkCell entry={entry} flight={flightById.get(entry.flightId)} />
                    </td>
                    {columns.map((column) => {
                      const key = cellKey(entry.id, column.id);
                      const isEditing =
                        editing?.entryId === entry.id && editing.columnId === column.id;
                      const isSelected =
                        selected?.entryId === entry.id && selected.columnId === column.id;
                      const isDerived = entry.derivedFields.includes(column.id);
                      const isInvalid = invalidCell === key;
                      const locked = !entry.isDraft;
                      const readOnly = column.readOnly === true;
                      const flagReason = flags.get(column.id);

                      if (isEditing) {
                        return (
                          /*
                           * The frozen width is not decoration. An `<input>` set to
                           * `w-full min-w-0` contributes nothing to an auto table's
                           * column sizing, where the button it replaces contributes its
                           * whole un-wrapped text, and the grid is nearly always wider
                           * than the screen — so the table sits at its min-content width
                           * and the `w-*` class on the column is a preference the
                           * browser has no room to honour. Without this the column
                           * collapsed to the width of its header the moment an edit
                           * opened: on a phone the Date column went 94px → 42px and the
                           * value being typed scrolled out of sight inside its own box.
                           */
                          <td
                            key={column.id}
                            className="p-0"
                            style={editWidth === null ? undefined : { minWidth: editWidth }}
                          >
                            <input
                              ref={(el) => {
                                registerCell(key, el);
                              }}
                              autoFocus
                              value={editText}
                              inputMode={column.input?.inputMode}
                              maxLength={column.input?.maxLength}
                              placeholder={column.input?.placeholder}
                              className={`${CELL_INPUT_CLASS} ${column.align === 'right' ? 'tabular text-right' : ''} ${flagReason === undefined ? '' : 'bg-danger/10'} ${isInvalid ? 'outline-danger outline-2 -outline-offset-2' : ''}`}
                              onChange={(e) => {
                                const typed = e.target.value;
                                setEditText(
                                  column.input === undefined ? typed : column.input.sanitise(typed),
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void commitAndMove(entry.id, column.id, editText, 1, 0);
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  void commitAndMove(
                                    entry.id,
                                    column.id,
                                    editText,
                                    0,
                                    e.shiftKey ? -1 : 1,
                                  );
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  cancelEdit(entry.id, column.id);
                                }
                              }}
                              onBlur={() => {
                                void commit(entry.id, column.id, editText).then((ok) => {
                                  if (ok) {
                                    setEditing(null);
                                  }
                                });
                              }}
                            />
                          </td>
                        );
                      }

                      return (
                        <td key={column.id} className="p-0">
                          <button
                            type="button"
                            ref={(el) => {
                              registerCell(key, el);
                            }}
                            onFocus={() => {
                              setSelected({ entryId: entry.id, columnId: column.id });
                            }}
                            onPointerDown={(e) => {
                              // Read *before* the browser moves focus to this cell, so a
                              // fresh tap is told apart from a tap on a cell that was
                              // already selected — see `onClick` below.
                              wasFocused.current = document.activeElement === e.currentTarget;
                            }}
                            onClick={(e) => {
                              // A keyboard user selects with one click and edits by then
                              // pressing Enter or typing — see `onKeyDown` below — but a
                              // touch screen has no "then": tapping a cell only ever fires
                              // a click, never a keydown, so without this a phone or tablet
                              // could select a cell but never open it. Tapping an
                              // already-selected cell opens it, which keeps a first tap a
                              // plain select (so checking rows and reading values isn't one
                              // slip from an accidental edit) while still reaching the
                              // editor with taps alone. It also gives desktop a working
                              // double-click, for free, as the second click of the pair.
                              if (wasFocused.current && !locked && !readOnly) {
                                startEdit(entry.id, column.id, column.read(entry));
                                return;
                              }
                              /*
                               * Focused here rather than left to the browser, because
                               * iOS Safari does not focus a `<button>` that is tapped —
                               * and the whole grid hangs off that focus. Without this a
                               * phone could select nothing (`onFocus` never fired, so no
                               * cell ever looked current) and open nothing (the probe
                               * above answered "not focused" however many times a cell
                               * was tapped), which read as a grid that had gone dead:
                               * the only cell that ever worked was the one the app
                               * focused itself on arrival, and only until the edit it
                               * opened was left again.
                               *
                               * Selection follows from the focus, through `onFocus`
                               * above, so it stays in one place rather than two.
                               */
                              e.currentTarget.focus();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                moveFocus(entry.id, column.id, -1, 0);
                              } else if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                moveFocus(entry.id, column.id, 1, 0);
                              } else if (e.key === 'ArrowLeft') {
                                e.preventDefault();
                                moveFocus(entry.id, column.id, 0, -1);
                              } else if (e.key === 'ArrowRight') {
                                e.preventDefault();
                                moveFocus(entry.id, column.id, 0, 1);
                              } else if (e.key === 'Enter' || e.key === 'F2') {
                                e.preventDefault();
                                if (!locked && !readOnly) {
                                  startEdit(entry.id, column.id, column.read(entry));
                                }
                              } else if (e.key === 'Backspace' || e.key === 'Delete') {
                                e.preventDefault();
                                if (!locked && !readOnly) {
                                  void commit(entry.id, column.id, '');
                                }
                              } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
                                e.preventDefault();
                                void undo();
                              } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') {
                                e.preventDefault();
                                void fillDown();
                              } else if (
                                e.key.length === 1 &&
                                !e.metaKey &&
                                !e.ctrlKey &&
                                !e.altKey
                              ) {
                                // Must be prevented here, not left to fall through: focus
                                // moves to the new input in this same synchronous commit,
                                // and an un-prevented keydown still delivers its default
                                // text-insertion (or, for Space, a button click) to
                                // whatever now has focus — typing the character twice.
                                e.preventDefault();
                                if (!locked && !readOnly) {
                                  startEdit(entry.id, column.id, e.key);
                                }
                              }
                            }}
                            className={`${CELL_CLASS} ${column.align === 'right' ? 'tabular justify-end' : ''} ${isDerived ? 'text-muted-foreground underline decoration-dotted underline-offset-2' : ''} ${flagReason === undefined ? '' : 'bg-danger/10 text-danger outline-danger/50 outline outline-1 -outline-offset-1'} ${locked || readOnly ? 'cursor-not-allowed' : 'cursor-cell'} ${isSelected ? 'outline-ring bg-accent-soft/60 relative z-10 outline outline-2 -outline-offset-2' : ''} ${isInvalid ? 'outline-danger outline-2 -outline-offset-2' : ''}`}
                            title={
                              locked
                                ? 'Approved. Move this entry back to draft to edit it.'
                                : readOnly
                                  ? 'Computed from the Class cell and the day/night hours. Not typed directly.'
                                  : (flagReason ??
                                    (isDerived
                                      ? 'From the imported track. Type over it to confirm the value.'
                                      : undefined))
                            }
                          >
                            {column.read(entry) || '—'}
                          </button>
                        </td>
                      );
                    })}
                    <td className="text-muted-foreground tabular px-2 py-1 text-right">
                      {formatDecimalHours(running?.total ?? 0)}
                      {running?.counted === false ? (
                        <span title="Not yet approved. Not counted in the total.">*</span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <HorizontalScrollbar
          targetRef={scrollAreaRef}
          controlsId={SCROLL_AREA_ID}
          label="Scroll the logbook columns"
        />
      </div>
    </div>
  );
}
